import { createTheme, darken, lighten } from '@material-ui/core/styles'; // view colorManipulator functions from MUI here: https://github.com/mui/material-ui/blob/v4.x/packages/material-ui/src/styles/colorManipulator.js
import red from '@material-ui/core/colors/red';
import pink from '@material-ui/core/colors/pink';
import purple from '@material-ui/core/colors/purple';
import blue from '@material-ui/core/colors/blue';
import teal from '@material-ui/core/colors/teal';
import green from '@material-ui/core/colors/green';
import yellow from '@material-ui/core/colors/yellow';
import orange from '@material-ui/core/colors/orange';
import grey from '@material-ui/core/colors/grey';
import type { Theme, ThemeOptions } from '@material-ui/core/styles/createTheme';
import type { CSSProperties } from '@material-ui/core/styles/withStyles';

const iconSizes: Theme['iconSizes'] = {
  small: '.875rem',
  medium: '1.125rem',
  large: '2rem',
};

const {
  palette: defaultPalette,
  spacing,
  shadows,
} = createTheme({
  palette: {
    contrastThreshold: 4.5, // AA a11y standards for text contrast ratio

    /* **** Main MUI Colors **** */
    primary: {
      main: '#00205B',
    },
    secondary: {
      main: '#166FC8',
    },
    info: {
      main: '#166FC8',
    },
    success: {
      light: '#00B407',
      main: '#01801D',
      dark: '#236731',
    },
    warning: {
      main: '#C34515',
    },
    error: {
      main: '#D73737',
    },

    grey,

    /* **** Custom Colors **** */
    tertiary: {
      light: '#f6685e',
      main: '#F25645',
      dark: '#9c4336',
      contrastText: '#FFFFFF',
    },

    rewards: {
      main: '#9F52CE',
      light: '#B275D8',
      dark: '#722C9D',
      contrastText: '#FFFFFF',
    },

    text: {
      secondary: grey[700],
    },

    focusIndicator: '#166FC8',

    scoreTier: {
      1: '#236731',
      2: '#00205B',
      3: '#C34515',
    },

    pink,
    red,
    orange,
    yellow,
    green,
    teal,
    blue,
    purple,
  },
});

export function getDefaultClassOverrides(
  palette: Theme['palette'] = defaultPalette
): ThemeOptions['overrides'] {
  // generate MUI Class overrides based on a given set of palette colors
  return {
    MuiCssBaseline: {
      '@global': {
        '*:focus-visible': {
          outlineColor: palette.focusIndicator, // catch-all for non MUI components ex: buttons used inside azure maps
          outlineWidth: 2,
          outlineStyle: 'solid',
        },

        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          '*.high-contrast-mode-border': {
            border: '1px solid CanvasText',
          },
        },
      },
    },
    MuiDrawer: {
      root: {
        zIndex: '1600!important' as CSSProperties['zIndex'], // !important is needed because it overrides an inline style, so !important is the only way to ensure the z-index is applied
      },
    },
    MuiToolbar: {
      regular: {
        '@media (min-width: 0px) and (orientation: landscape) and (max-width: 600px)': {
          minHeight: 56,
        },
      },
    },
    MuiLink: {
      root: {
        '&$focusVisible': {
          outlineColor: palette.focusIndicator,
        },
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          '& svg, & .MuiSvgIcon-root': {
            color: 'LinkText',
          },
          '&$button': {
            /*
            This is a specific MAS requirement that was called out by the microsoft auditors. Per their internal guidelines,
            any button element in high contrast mode needs to have ButtonText color, and be differentiated from links.
            We are ensuring that <Link component="button"> has ButtonText colors, and adding a border to differentiate it from links.
            */
            color: 'ButtonText',
            backgroundColor: 'ButtonFace',
            border: '1px solid ButtonBorder',
            borderRadius: 6,
            '& svg, & .MuiSvgIcon-root': {
              color: 'ButtonText',
            },
            '&:focus': {
              outlineColor: 'Highlight',
              outlineStyle: 'solid',
              outlineWidth: 2,
            },
          },
        },
      },
      button: {
        '&$focusVisible': {
          outlineColor: palette.focusIndicator,
          outlineOffset: 2,
        },
      },
    },

    /* ******************** */
    /* ***** Buttons ****** */
    /* ******************** */
    MuiButtonBase: {
      root: {
        fontFamily: 'Inter, Arial, sans-serif',
        overflowY: 'hidden',
        overflowX: 'visible',
        '&$focusVisible': {
          outlineWidth: 2,
          outlineStyle: 'solid',
          outlineColor: palette.focusIndicator,
        },
      },
    },
    MuiButton: {
      root: {
        fontWeight: 600,
        borderRadius: 50,
        lineHeight: 'inherit',
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          '&$contained, &$outlined, &$outlined:hover': {
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: 'ButtonText',
          },
          '&.Mui-disabled': {
            borderColor: 'GrayText',
            color: 'GrayText',
            '& svg': {
              color: 'GrayText',
            },
          },
          '&$focusVisible': {
            outlineColor: 'Highlight',
            outlineWidth: 3,
          },
        },
      },
      iconSizeSmall: {
        '& > *:first-child': { fontSize: iconSizes.small },
      },
      iconSizeMedium: {
        '& > *:first-child': { fontSize: iconSizes.medium },
      },
      iconSizeLarge: {
        '& > *:first-child': { fontSize: iconSizes.large },
      },
      contained: {
        paddingLeft: spacing(3),
        paddingRight: spacing(3),
        '&$focusVisible': {
          outlineWidth: 3,
          outlineOffset: 0,
          outlineColor: palette.grey[700],
          boxShadow: `0 0 0 5px ${palette.common.white} !important`, // !important is necessary because by default, disableElevation will remove all box shadow and has higher specificity
        },
        '&.tertiary': {
          backgroundColor: palette.tertiary.main,
          '&.Mui-disabled': {
            backgroundColor: palette.action.disabledBackground,
          },
          '&:hover': {
            backgroundColor: palette.tertiary.dark,
          },
          '&.Mui-focusVisible': {
            outlineColor: lighten(palette.tertiary.main, 0.1),
            backgroundColor: darken(palette.tertiary.main, 0.6),
          },
        },
        '&.rewards': {
          backgroundColor: palette.rewards.main,
          '&.Mui-disabled': {
            backgroundColor: palette.action.disabledBackground,
          },
          '&:hover': {
            backgroundColor: palette.rewards.dark,
          },
          '&.Mui-focusVisible': {
            outlineColor: lighten(palette.rewards.main, 0.1),
            backgroundColor: darken(palette.rewards.main, 0.6),
          },
        },
      },
      containedPrimary: {
        '&$focusVisible': {
          outlineColor: lighten(palette.primary.main, 0.5),
          backgroundColor: darken(palette.primary.main, 0.3),
        },
      },
      containedSecondary: {
        '&$focusVisible': {
          outlineColor: darken(palette.secondary.main, 0.5),
        },
      },
      outlined: {
        paddingLeft: spacing(3),
        paddingRight: spacing(3),
        '&$focusVisible': {
          outlineColor: palette.grey[700],
        },
        '&.white-background': {
          backgroundColor: palette.common.white,
        },
      },
      outlinedPrimary: {
        borderColor: palette.primary.main, // by default, MUI uses a .light color for the border unless hovered, override that
        '&$focusVisible': {
          outlineColor: palette.primary.main,
        },
        '&.white-background:hover': {
          backgroundColor: darken(palette.common.white, 0.1),
        },
      },
      outlinedSecondary: {
        borderColor: palette.secondary.main, // by default, MUI uses a .light color for the border unless hovered, override that
        '&$focusVisible': {
          outlineColor: palette.secondary.main,
        },
        '&.white-background:hover': {
          backgroundColor: darken(palette.common.white, 0.1),
          borderColor: palette.primary.dark,
          color: palette.primary.dark,
        },
      },
    },
    MuiFab: {
      root: {
        '&$focusVisible': {
          outlineOffset: 1,
          outlineColor: palette.grey[700],
        },
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: 'ButtonText',
        },
      },
      primary: {
        '&$focusVisible': {
          outlineColor: lighten(palette.primary.main, 0.5),
          backgroundColor: darken(palette.primary.main, 0.3),
        },
      },
      secondary: {
        '&$focusVisible': {
          outlineColor: lighten(palette.secondary.main, 0.5),
          backgroundColor: darken(palette.secondary.main, 0.3),
        },
      },
    },
    MuiIconButton: {
      root: {
        '&.Mui-focusVisible': {
          outlineOffset: -2,
        },
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          '&.Mui-disabled $label > *': {
            color: 'GrayText',
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      root: {
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          '& > button.Mui-selected': {
            backgroundColor: 'SelectedItem',
          },
        },
      },
    },
    MuiDivider: {
      root: {
        margin: '1rem 0',
      },
    },
    MuiPopover: {
      paper: {
        padding: '0.5rem',
      },
    },
    /* ******************** */
    /* ***** Inputs ****** */
    /* ******************** */
    MuiTextField: {
      root: {
        // custom rounded class for outlined variant only
        '&.rounded': {
          '& .MuiOutlinedInput-notchedOutline': { borderRadius: 32 },
          '& .MuiOutlinedInput-input': {
            marginLeft: spacing(0.5),
            paddingLeft: spacing(2),
            paddingRight: spacing(2),
          },
          '& .MuiOutlinedInput-input.MuiOutlinedInput-inputAdornedStart': {
            marginLeft: 0,
            paddingLeft: 0,
          },
        },

        // custom square class
        '&.square': {
          '& .MuiOutlinedInput-notchedOutline': { borderRadius: 0 },
        },
      },
    },

    MuiFormLabel: {
      root: {
        margin: '0.5rem 0',
        color: palette.text.secondary,
        fontWeight: 'bold',
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          '&.Mui-disabled': {
            color: 'GrayText',
          },
        },
      },
      colorSecondary: {},
    },
    MuiFormControlLabel: {
      root: {
        '@media (forced-colors: active)': {
          '&.Mui-disabled $label': {
            // MS Edge high contrast modes
            color: 'GrayText',
          },
        },
      },
    },
    MuiInputBase: {
      input: {
        fontSize: '1rem',
        '&::placeholder': {
          fontSize: '1rem',
          opacity: 0.8,
        },
      },
      root: {
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          '&.MuiInput-underline:after, &.MuiFilledInput-underline:after': {
            borderBottomColor: 'Highlight',
          },
        },
      },
    },
    MuiInputLabel: {
      root: {
        margin: 0,
        textTransform: 'none',
        fontWeight: 'normal',
      },
      shrink: {
        background: palette.background.paper,
        paddingLeft: 2,
      },
      outlined: {
        // the reason these transform properties need to be modified, is because we have globally changed the MuiInputBase font size
        transform: 'translate(14px, 21px) scale(1)',
        '&$marginDense': {
          transform: 'translate(14px, 13px) scale(1)',
        },
      },
    },
    MuiSelect: {
      select: {
        '&:focus': {
          backgroundColor: 'inherit',
        },
      },
    },
    MuiOutlinedInput: {
      root: {
        '&.Mui-focused $notchedOutline': {
          borderColor: palette.focusIndicator,
        },
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          '&.Mui-focused $notchedOutline, &.Mui-focused$colorSecondary $notchedOutline': {
            borderColor: 'Highlight',
          },
          '&.Mui-focused legend, &.Mui-focused$colorSecondary legend': {
            color: 'Highlight',
          },
          '&.Mui-disabled $notchedOutline, &.Mui-disabled legend': {
            borderColor: 'GrayText',
          },
        },
      },
      notchedOutline: {
        borderRadius: 10,
        borderColor: palette.grey[600],
      },
      colorSecondary: {},
    },
    MuiFormGroup: {
      root: {
        padding: '0.5rem 1rem',
      },
    },
    MuiPaper: {
      root: {
        '&.grey': {
          backgroundColor: palette.grey[100],
        },
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          border: '1px solid CanvasText',
          'button&': {
            // covers instances where Paper is given component="button" in high contrast mode (example: <TopSearches />)
            border: '1px solid ButtonText',
            color: 'ButtonText',
          },

          'a&': {
            border: '1px solid LinkText',
            color: 'LinkText',
          },

          '&:focus': {
            outlineColor: 'Highlight',
            outlineWidth: 3,
          },
        },
      },
      rounded: {
        borderRadius: 12,
      },
    },
    MuiDialogContent: {
      root: {
        '&:first-child': {
          paddingTop: 8,
        },
      },
    },
    MuiDialogTitle: {
      root: {
        padding: '16px 24px 8px',
        textAlign: 'center',
      },
    },
    MuiSnackbar: {
      root: {
        zIndex: 1600,
      },
    },
    MuiCircularProgress: {
      colorPrimary: {
        color: palette.tertiary.main,
      },
    },
    MuiMenuItem: {
      root: {
        '&.Mui-focusVisible': {
          outline: `2px solid ${palette.focusIndicator}`,
          background: 'none',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
        '&.Mui-focusVisible.Mui-selected': {
          background: '#e0e0e0',
        },
      },
    },
    MuiListItem: {
      root: {
        '&$selected': {
          // providing an underline allows for a non-color visual indicator.
          // without it, we would be required to have a 3:1 contrast ratio with the background
          textDecoration: 'underline',
        },
      },
    },
    MuiAutocomplete: {
      option: {
        '&[aria-selected="true"]': {
          // providing an underline allows for a non-color visual indicator.
          // without it, we would be required to have a 3:1 contrast ratio with the background
          textDecoration: 'underline',
        },
        '&[data-focus="true"]': {
          outlineOffset: -3,
          outline: `2px solid ${palette.focusIndicator}`,
        },
      },
    },
    MuiSvgIcon: {
      fontSizeSmall: { fontSize: iconSizes.small },
      fontSizeLarge: { fontSize: iconSizes.large },

      root: {
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          '&:not($colorDisabled)': {
            color: 'CanvasText',
          },
        },
      },
    },
    MuiTab: {
      root: {
        '&.Mui-focusVisible': {
          outlineOffset: -1 * spacing(0.75),
        },
      },
    },
    MuiTabs: {
      indicator: {
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          backgroundColor: 'Highlight',
          height: 4,
        },
      },
    },
    MuiRadio: {
      root: {
        '&.MuiButtonBase-root.Mui-focusVisible': {
          outline: 'none',
        },
        '&.Mui-focusVisible > .MuiIconButton-label': {
          outline: `2px solid ${palette.focusIndicator}`,
        },

        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          '&.Mui-focusVisible .MuiIconButton-label': {
            outlineColor: 'Highlight',
          },
          '&.Mui-disabled .MuiSvgIcon-root': {
            color: 'GrayText',
          },
        },
      },
    },
    MuiCheckbox: {
      root: {
        '&.MuiButtonBase-root.Mui-focusVisible': {
          outline: 'none',
        },
        '&.Mui-focusVisible > .MuiIconButton-label': {
          outline: `2px solid ${palette.focusIndicator}`,
        },

        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          '& input, & input:focus': { appearance: 'none', outline: 'none' },
          '&.Mui-focusVisible > .MuiIconButton-label': {
            outlineColor: 'Highlight',
          },
          '&.Mui-disabled .MuiSvgIcon-root': {
            color: 'GrayText',
          },
        },
      },
    },
    MuiSwitch: {
      root: {
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          '& input, & input:focus': {
            appearance: 'none',
            outline: 'none',
          },
          '& $switchBase.Mui-disabled $thumb': {
            backgroundColor: 'GrayText',
          },
          '& $switchBase.Mui-focusVisible': {
            outlineColor: 'Highlight',
          },
          '& $thumb': {
            backgroundColor: 'FieldText',
          },
          '& $track': {
            backgroundColor: 'FieldText !important', // !important is necessary due to the various combinations of color, checked, and disabled states that may override this if important is not present
            // without !important, the selector required would be incredibly long and difficult to read
          },
        },
      },
    },
    MuiSlider: {
      root: {
        '&.Mui-disabled $markLabel': {
          color: palette.action.disabled,
        },
        '&.MuiSlider-root.MuiSlider-vertical': {
          padding: '0px',
          '&.MuiSlider-vertical .MuiSlider-thumb': {
            marginLeft: -2.3,
            width: 15,
            height: 15,
          },
        },
        '&.MuiSlider-vertical .MuiSlider-rail': {
          width: '10px',
        },
        '&.MuiSlider-vertical .MuiSlider-track': {
          width: '10px',
          bottom: -1,
        },
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          '& $track, & $rail, &$trackInverted $rail': {
            backgroundColor: 'FieldText',
          },
          '&$trackInverted $track': {
            backgroundColor: 'GrayText',
          },
          '&.Mui-disabled': {
            '& $track, $rail': {
              backgroundColor: 'GrayText',
            },
            '& $thumb': {
              borderColor: 'GrayText',
            },
          },
          '& $thumb.Mui-focusVisible': {
            outlineColor: 'Highlight',
            outlineWidth: 5,
            outlineOffset: 2,
            outlineStyle: 'solid',
          },
        },
      },
      // slider customized to follow this design: https://www.figma.com/design/GhY79G8MF8Qfmjmus4UYly/Filter-and-Search-UX-Exploration?node-id=1650-3289&p=f&t=yOl2BBe6yADXWtle-0
      track: { height: 12, borderRadius: 6 },
      rail: {
        height: 8,
        backgroundColor: grey[500],
        marginTop: 2,
        borderRadius: 6,
      },
      trackInverted: {
        '& $track': {
          // match rail styles
          height: 8,
          backgroundColor: grey[500],
          marginTop: 2,
          borderRadius: 6,
        },
        '& $rail': {
          backgroundColor: palette.primary.main,
        },
      },

      colorSecondary: {
        '&$trackInverted $rail': {
          backgroundColor: palette.secondary.main,
        },
      },

      mark: {
        // the "ticks" shown as stopping points for the thumb
        marginTop: 2, // match rail position
        height: 8, // match rail height
        '&[data-index="0"]': { display: 'none' }, // hides the first tick
        '&:has(+ $thumb), &:has(+ $markLabel + $thumb)': { display: 'none' }, // hides the last tick - selecting the last tick by assuming it will be followed by the thumb, or followed by a label that is followed by the thumb

        '&.hidden': { display: 'none' },
      },
      markActive: {
        marginTop: 0, // match track position
        height: 12, // match track height
      },
      thumb: {
        marginTop: -7,
        height: 24,
        width: 24,
        color: palette.background.paper,
        border: `2px solid ${palette.grey[700]}`,

        '&.Mui-disabled': {
          marginTop: -7,
          height: 24,
          width: 24,
          borderColor: palette.action.disabled,

          '& $valueLabel > span > span': {
            color: palette.action.disabled,
          },
        },
      },

      thumbColorPrimary: {
        borderColor: palette.primary.main,

        '& $valueLabel > span > span': {
          color: palette.primary.main,
        },
      },

      thumbColorSecondary: {
        borderColor: palette.secondary.main,
        '& $valueLabel > span > span': {
          color: palette.secondary.main,
        },
      },

      valueLabel: {
        // the "popup" that appears on focus or hover of the thumb
        left: `calc(-50% + 4px)`,
        top: -28,
        color: 'transparent',
        '& > span': {
          // the PrivateValueLabel-circle
          color: 'inherit',
        },
      },
      marked: {
        marginBottom: spacing(4), // space for mark labels
      },
      markLabel: {
        marginTop: spacing(2),
      },
    },
    MuiRating: {
      sizeLarge: { fontSize: '2.5rem' },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: palette.common.white,
        color: palette.text.primary,
        fontSize: '.75rem',
        boxShadow: shadows[8],
        padding: spacing(1),
        borderRadius: spacing(1),
      },
      arrow: {
        color: palette.common.white,
      },
    },
    MuiAccordionSummary: {
      root: {
        '&.Mui-expanded': { minHeight: 0 },
        '&.Mui-focusVisible': { backgroundColor: 'inherit' },
      },
      content: {
        '&.Mui-expanded': { margin: '12px 0' }, // match the margin of collapsed size
      },
    },
    MuiAvatar: {
      root: {
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          border: '1px solid CanvasText',
        },
      },
    },
    MuiAlert: {
      root: {
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          border: '1px solid CanvasText',
        },
      },
    },
    MuiCard: {
      root: {
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          border: '1px solid CanvasText',
          'a&': { borderColor: 'LinkText' },
          'button&': { borderColor: 'ButtonText' },
        },
      },
    },
    MuiAppBar: {
      root: {
        '@media (forced-colors: active)': {
          // MS Edge high contrast modes
          border: 'none',
          borderBottom: '1px solid CanvasText',
        },
      },
    },
    MuiSkeleton: {
      root: {
        '@media (forced-colors: active)': {
          backgroundColor: 'CanvasText',
          border: '1px solid CanvasText',
          '&$wave::after': {
            backgroundColor: 'Canvas',
          },
        },
      },
    },
  };
}

const defaultTheme: ThemeOptions = {
  props: {
    MuiButtonBase: {
      disableRipple: true,
    },
    MuiButton: {
      disableElevation: true,
    },
    MuiButtonGroup: {
      disableRipple: true,
      disableElevation: true,
    },
    MuiCheckbox: {
      color: 'primary',
    },
    MuiRadio: {
      color: 'primary',
    },
    MuiLink: {
      color: 'secondary',
      underline: 'always',
    },
    MuiBadge: {
      overlap: 'rectangular', // resolves deprecation warning
    },
    MuiSwitch: {
      color: 'primary',
    },
    MuiSkeleton: {
      animation: 'wave',
    },
    MuiTextField: {
      variant: 'outlined',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1920,
    },
  },
  typography: {
    fontFamily: 'Inter, Arial, sans-serif',
    h1: {
      fontSize: '2rem',
      fontWeight: 600, // equivalent to 'semibold'
      margin: '0.5rem 0',
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      lineHeight: 1.15,
      margin: '0.5rem 0',
    },
    h3: {
      fontSize: '1rem',
      fontWeight: 'bold',
      margin: '0.25rem 0',
    },
    h4: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1rem',
    },
    h6: {
      fontSize: '.875rem',
    },
    body1: {
      fontSize: '.875rem',
    },
    body2: {
      fontSize: '.875rem',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    subtitle2: {
      fontSize: '.875rem',
      fontWeight: 600,
    },
    caption: {
      fontSize: '0.8125rem',
      lineHeight: 1.25,
      display: 'inline-block',
    },
    button: {
      textTransform: 'none',
    },
  },
  palette: defaultPalette,
  overrides: getDefaultClassOverrides(),
  iconSizes,
};

export default defaultTheme;
